import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function RegisterPage() {
  useDocumentTitle('Create Account');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [barNumber, setBarNumber] = useState('');
  const [firmName, setFirmName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { error: signupError } = await signup(email, password, fullName, barNumber, firmName);
      if (signupError) {
        setError(signupError.message);
        return;
      }
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper-50 p-4">
      <div className="w-full max-w-md bg-white border border-paper-300 p-6" role="main" aria-label="Create your account">
        <div className="mb-6 space-y-1 text-center">
          <h1 className="font-serif text-4xl font-bold text-navy-900">Create your account</h1>
          <p className="text-sm text-paper-500">Create your attorney account</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {error && (
            <div className="bg-red-50 p-3 text-sm text-red-600" role="alert" aria-live="assertive">
              {error}
            </div>
          )}
          <div className="space-y-2">
            <label htmlFor="fullName" className="text-sm font-medium">Full Name</label>
            <input
              id="fullName"
              type="text"
              placeholder="John Smith"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full border border-paper-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-forest-500"
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium">Email</label>
            <input
              id="email"
              type="email"
              placeholder="name@lawfirm.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-paper-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-forest-500"
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="barNumber" className="text-sm font-medium">Bar Number</label>
            <input
              id="barNumber"
              type="text"
              placeholder="12345678"
              value={barNumber}
              onChange={(e) => setBarNumber(e.target.value)}
              className="w-full border border-paper-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-forest-500"
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="firmName" className="text-sm font-medium">Law Firm Name (optional)</label>
            <input
              id="firmName"
              type="text"
              placeholder="Smith & Associates"
              value={firmName}
              onChange={(e) => setFirmName(e.target.value)}
              className="w-full border border-paper-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-forest-500"
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="password" className="text-sm font-medium">Password</label>
            <input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-paper-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-forest-500"
              required
              minLength={8}
            />
          </div>
          <button
            type="submit"
            className="w-full bg-navy-900 hover:bg-navy-800 text-white font-medium px-5 py-3"
            disabled={isLoading}
          >
            {isLoading ? 'Creating account...' : 'Create account'}
          </button>
        </form>

        <div className="mt-6 text-center text-sm">
          <a
            href="/login"
            className="text-forest-500 hover:text-forest-600 hover:underline"
          >
            Already have an account? Sign in
          </a>
        </div>
      </div>
    </div>
  );
}
